/**
 * More → Export data: everything this phone holds, as one ZIP the office can open — the work
 * still waiting or already sent (outbox), purchases in progress (drafts), and every photo still
 * on the phone. Payloads are opened with this phone's key first (they are sealed at rest). Full
 * bank account numbers and PIN hashes never leave the phone in the file.
 */
import { zipSync, strToU8 } from 'fflate'
import { getDb, type MediaRecord, type SupplyChainDB } from './db'
import { getMeta } from './meta'
import { openDrafts, openOutboxRecords } from './sealed'

export interface ExportSummary {
  records: number
  drafts: number
  photos: number
  /** Photos already uploaded and removed from the phone to free space (not in the file). */
  photosUploadedOnly: number
  bytes: number
  fileName: string
}

/** Keys whose values are never written in full. */
const SECRET_KEY =
  /^(account_?number|accountnumber|pin|pin_hash|pinhash|vault_key.*|token|password)$/i

/** A copy with secrets masked: account numbers keep their last 4 digits, the rest go. */
export function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact)
  if (!value || typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY.test(k)) {
      out[k] =
        /account/i.test(k) && typeof v === 'string' && v.length > 4
          ? `••••${v.replace(/\D/g, '').slice(-4)}`
          : v == null
            ? v
            : '[removed]'
    } else out[k] = redact(v)
  }
  return out
}

const cell = (v: unknown) => {
  const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
export const toCsv = (head: string[], rows: unknown[][]) =>
  [head, ...rows].map((r) => r.map(cell).join(',')).join('\r\n')

const iso = (ms: number | null | undefined) => (ms ? new Date(ms).toISOString() : '')

const EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
}

async function blobBytes(b: Blob): Promise<Uint8Array> {
  if (typeof b.arrayBuffer === 'function') return new Uint8Array(await b.arrayBuffer())
  // Older WebViews: Blob.arrayBuffer() is missing.
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer))
    r.onerror = () => reject(r.error ?? new Error('Could not read a photo'))
    r.readAsArrayBuffer(b)
  })
}

/** Builds the ZIP bytes (testable; `downloadExport` saves it). */
export async function buildExport(
  db: SupplyChainDB = getDb(),
  now = new Date(),
): Promise<{ zip: Uint8Array; summary: Omit<ExportSummary, 'bytes' | 'fileName'> }> {
  const [outboxRaw, draftRows, media, device, lastSync] = await Promise.all([
    db.outbox.orderBy('seq').toArray(),
    db.drafts.toArray(),
    db.media.toArray(),
    getMeta('device', db),
    getMeta('last_sync_at', db),
  ])
  const outbox = await openOutboxRecords(outboxRaw, db)
  const drafts = await openDrafts(draftRows, db)

  const files: Record<string, Uint8Array | [Uint8Array, { level: 0 }]> = {}
  const text = (name: string, body: string) => (files[name] = strToU8(body))

  const deviceInfo = device as
    { team?: { name?: string; code?: string }; code_prefix?: string } | null | undefined
  text(
    'device.json',
    JSON.stringify(
      redact({
        exported_at: now.toISOString(),
        app_version: __APP_VERSION__,
        team: deviceInfo?.team ?? null,
        code_prefix: deviceInfo?.code_prefix ?? null,
        last_sync_at: lastSync ?? null,
        counts: { outbox: outbox.length, drafts: drafts.length, media: media.length },
      }),
      null,
      2,
    ),
  )

  // Work made on this phone, in the order it was made.
  text(
    'outbox.csv',
    toCsv(
      [
        'Seq',
        'Command ID',
        'Type',
        'Status',
        'Created',
        'Occurred',
        'Session',
        'Attempts',
        'Last error',
        'Photos',
        'Locked',
      ],
      outbox.map((r) => [
        r.seq,
        r.id,
        r.type,
        r.status,
        iso(r.created_at),
        r.occurred_at,
        r.session_id,
        r.attempts,
        r.last_error,
        r.media_ids.join(' '),
        r.payload_locked ? 'yes' : '',
      ]),
    ),
  )
  text(
    'outbox.json',
    JSON.stringify(
      outbox.map((r) => redact({ ...r, payload: r.payload })),
      null,
      2,
    ),
  )
  text(
    'drafts.json',
    JSON.stringify(
      drafts.map((d) => redact(d)),
      null,
      2,
    ),
  )

  // Photos still on the phone (an uploaded photo's file is dropped to free space).
  let photos = 0
  let uploadedOnly = 0
  const mediaRows: unknown[][] = []
  for (const m of media as MediaRecord[]) {
    let file = ''
    if (m.blob) {
      file = `photos/${m.id}.${EXT[m.mime] ?? 'bin'}`
      // JPEGs do not compress: store them as they are (fast, little memory).
      files[file] = [await blobBytes(m.blob), { level: 0 }]
      photos += 1
    } else if (m.status === 'UPLOADED') uploadedOnly += 1
    mediaRows.push([m.id, m.status, m.mime, m.size, iso(m.created_at), iso(m.uploaded_at), file])
  }
  text(
    'photos.csv',
    toCsv(
      ['Photo ID', 'Status', 'Type', 'Size (bytes)', 'Taken', 'Uploaded', 'File in this ZIP'],
      mediaRows,
    ),
  )

  text(
    'README.txt',
    [
      'Uncompromised supply chain - phone data export',
      `Exported: ${now.toISOString()}  App: v${__APP_VERSION__}`,
      '',
      'outbox.csv / outbox.json  Every record made on this phone and its sync status',
      '                          (PENDING / SENT etc.). JSON has the full content.',
      'drafts.json               Purchases or tasks still in progress on this phone.',
      'photos.csv, photos/       Photos still on the phone. Photos already uploaded',
      '                          were removed from the phone and are on the server.',
      '',
      'Bank account numbers are cut to their last 4 digits; PINs and keys are removed.',
    ].join('\r\n'),
  )

  const zip = zipSync(files, { level: 6 })
  return {
    zip,
    summary: {
      records: outbox.length,
      drafts: drafts.length,
      photos,
      photosUploadedOnly: uploadedOnly,
    },
  }
}

/** Builds the ZIP and saves it (a download on Android; the share sheet where that is all there is). */
export async function downloadExport(db: SupplyChainDB = getDb()): Promise<ExportSummary> {
  const now = new Date()
  const { zip, summary } = await buildExport(db, now)
  const stamp = now.toISOString().slice(0, 16).replace(/[:T]/g, '-')
  const fileName = `uc-phone-export_${stamp}.zip`
  const blob = new Blob([zip as Uint8Array<ArrayBuffer>], { type: 'application/zip' })
  const file =
    typeof File !== 'undefined' ? new File([blob], fileName, { type: 'application/zip' }) : null
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean }
  const isIos = /iP(hone|ad|od)/.test(navigator.userAgent)
  if (isIos && file && nav.canShare?.({ files: [file] })) {
    // iOS home-screen apps cannot download: share the file instead.
    await navigator.share({ files: [file], title: fileName })
  } else {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
  return { ...summary, bytes: zip.byteLength, fileName }
}
